import { Locales } from '../../models/locale/locales';

/** Every locale the app ships translations for. */
export const LOCALES: Locales[] = ['de-DE', 'fr-FR', 'it-IT', 'es-ES', 'nl-NL', 'be-FR', 'be-NL'];

export const isValidLocale = (locale: string | null | undefined): locale is Locales =>
  !!locale && LOCALES.includes(locale as Locales);

/**
 * The shipped locale a loosely-written locale string refers to, in its canonical
 * casing — or undefined when it names no locale we ship.
 *
 * Matching is case-insensitive and tolerates `_` for `-` because the partner
 * redirects that send visitors here do not all write the value the way we do.
 * Beko's register.beko.com redirect has always sent Spain as `?lang=es-es`
 * (five in six Beko Spain arrivals in the legacy project), and the legacy Beko
 * page matched it case-insensitively. Our exact-match check treated `es-es` as
 * unknown from the 21 Aug 2026 cutover, so those visitors got the German UI
 * fallback (German copy, German phone line, German online journey) and no
 * `locale`/`country` on their analytics events. Normalising here restores the
 * legacy behaviour without depending on Beko changing their redirect.
 *
 * Only the casing/separator is forgiven: an unknown locale still returns
 * undefined, so the "record no country rather than guess" rule below holds.
 */
export const normaliseLocale = (raw: string | null | undefined): Locales | undefined => {
  if (!raw) return undefined;
  const key = raw.trim().replace('_', '-').toLowerCase();
  return LOCALES.find((locale) => locale.toLowerCase() === key);
};

/**
 * The locale the visitor asked for via `?lang=`, or undefined when the param is
 * missing or names a locale we do not ship. Casing is normalised (see
 * `normaliseLocale`), so `?lang=es-es` reads as `es-ES`.
 *
 * Deliberately does NOT fall back to a default: analytics should record that a
 * visitor arrived without a usable `?lang=` rather than silently attribute them
 * to the base locale. The UI has its own fallback (see context/locale/provider.tsx).
 */
export const getUrlLocale = (search: string = window.location.search): Locales | undefined =>
  normaliseLocale(new URLSearchParams(search).get('lang'));

/**
 * Rewrites a mis-cased `?lang=` in the address bar to its canonical form (e.g.
 * `es-es` -> `es-ES`) without reloading, so the URL PostHog records on every
 * event, the language switcher's value and any link the visitor copies all
 * agree with the page actually rendered.
 *
 * Runs once at bootstrap, BEFORE PostHog initialises and before React renders,
 * so the first `$pageview` already carries the canonical URL. A missing,
 * unknown or already-canonical value leaves the URL untouched — in particular
 * an unknown locale is NOT rewritten to the default, for the reason given on
 * `getUrlLocale`.
 */
export const canonicaliseUrlLocale = (): void => {
  const url = new URL(window.location.href);
  const raw = url.searchParams.get('lang');
  const canonical = normaliseLocale(raw);
  if (!raw || !canonical || raw === canonical) return;
  url.searchParams.set('lang', canonical);
  window.history.replaceState(window.history.state, '', url);
};

/**
 * ISO 3166-1 alpha-2 country for a locale, for dashboards that group by country
 * rather than by locale string. Belgium is one country served in two languages,
 * so `be-FR` and `be-NL` both map to `BE`; the language is carried by the locale
 * string. The language tag is not a real region code, so it is mapped explicitly
 * rather than parsed.
 */
const LOCALE_COUNTRY: Record<Locales, string> = {
  'de-DE': 'DE',
  'fr-FR': 'FR',
  'it-IT': 'IT',
  'es-ES': 'ES',
  'nl-NL': 'NL',
  'be-FR': 'BE',
  'be-NL': 'BE',
};

export const getCountry = (locale: Locales | undefined): string | undefined =>
  locale ? LOCALE_COUNTRY[locale] : undefined;
